"use client";

import { useMemo, useCallback, useState, useEffect } from "react";
import {
	MaterialReactTable,
	useMaterialReactTable,
	type MRT_ColumnDef,
} from "material-react-table";
import { Badge } from "@/components/ui/Badge";
import {
	FileText,
	Lock,
	Sparkles,
	AlertCircle,
	Building2,
	ExternalLink,
} from "lucide-react";
import { isAuthenticatedSync } from "@/lib/auth";
import { useOnboarding } from "@/contexts/OnboardingContext";
import {
	exportPropertiesToExcel,
	exportPropertiesToPropstream,
} from "@/services/export-properties";
import ParcelDataViewer, {
	type ParcelData,
} from "@/components/dashboard/ParcelDataViewer";
import type { NormalizedPropertyRecord } from "@/services/properties-api";
import type { ScrapedParcelData } from "@/types/parcel";
import { normalizePropertyRecord } from "@/services/properties-api";
import type { AddressData } from "@/types/address";
import { useQuery } from "@tanstack/react-query";
import {
	fetchNearbyProperties,
	fetchNearbyPropertiesUnauthenticated,
	type NearbyPropertiesRequest,
	type NearbyPropertiesResponse,
} from "@/services/properties-api";
import { Loader2 } from "lucide-react";
import styles from "../dashboard/PropertiesTable.module.css";
import PropertyPhotosButton from "@/components/dashboard/PropertyPhotosButton";
import ViewPhotosModal from "@/components/dashboard/ViewPhotosModal";
import RequestPhotosModal from "@/components/dashboard/RequestPhotosModal";
import { checkPropertyMediaRequest } from "@/services/property-media-api";
import { toast } from "sonner";

const MAX_PLAYGROUND_RECORDS = 10;

interface PlaygroundTableProps {
	address?: AddressData | null;
	countyState?: string;
	onSignUpClick?: () => void;
	onLoadingChange?: (loading: boolean) => void;
	isUpgrading?: boolean;
	initialData?: NearbyPropertiesResponse;
}

export default function PlaygroundTable({
	address,
	countyState,
	onSignUpClick,
	onLoadingChange,
	isUpgrading,
}: PlaygroundTableProps) {
	const { isSubscribed } = useOnboarding();
	const [isSignedIn, setIsSignedIn] = useState(false);
	const [exportingType, setExportingType] = useState<
		"excel" | "propstream" | null
	>(null);
	const [isParcelViewerOpen, setIsParcelViewerOpen] = useState(false);
	const [selectedParcel, setSelectedParcel] = useState<ParcelData | null>(
		null
	);
	const [selectedProperty, setSelectedProperty] =
		useState<NormalizedPropertyRecord | null>(null);
	const [photoModalOpen, setPhotoModalOpen] = useState(false);
	const [checkingPhotoRequestId, setCheckingPhotoRequestId] = useState<
		string | null
	>(null);

	const handlePhotoClick = useCallback(
		async (record: NormalizedPropertyRecord) => {
			// When we already have photos, open the image view modal directly (no API call)
			const hasPhotos =
				Array.isArray(record?.medias) && record.medias.length > 0;
			if (hasPhotos) {
				setSelectedProperty(record);
				setPhotoModalOpen(true);
				return;
			}
			// No photos: check if user can request, then open request modal
			const id = record.propertyId ?? record.id;
			if (!id) return;
			setCheckingPhotoRequestId(id);
			try {
				const res = await checkPropertyMediaRequest(id);
				if (!res.canRequest) {
					toast.info("Request already initiated for this property.");
					return;
				}
				setSelectedProperty(record);
				setPhotoModalOpen(true);
			} catch (e) {
				toast.error(
					e instanceof Error ? e.message : "Failed to check request."
				);
			} finally {
				setCheckingPhotoRequestId(null);
			}
		},
		[]
	);

	useEffect(() => {
		const checkAuth = () => setIsSignedIn(isAuthenticatedSync());
		checkAuth();
		const interval = setInterval(checkAuth, 1000);
		return () => clearInterval(interval);
	}, []);

	// Parse county and state from countyState string (format: "County, State")
	const parsedCountyState = useMemo(() => {
		if (!countyState) return { county: undefined, state: undefined };
		const parts = countyState.split(",").map((p) => p.trim());
		if (parts.length === 2) {
			return { county: parts[0], state: parts[1] };
		}
		return { county: undefined, state: undefined };
	}, [countyState]);

	// Determine if we should use address or county/state
	const useAddress = Boolean(address?.latitude && address?.longitude);
	const useCountyState = Boolean(
		parsedCountyState.county && parsedCountyState.state
	);
	const canUseAuthenticatedApi = isSignedIn && isSubscribed;

	// Playground must remain usable without auth. Only subscribed users need the authenticated dataset.
	const { data, isLoading, isError, error } = useQuery({
		queryKey: [
			"playground-properties",
			canUseAuthenticatedApi,
			useAddress ? address?.latitude : null,
			useAddress ? address?.longitude : null,
			useCountyState ? parsedCountyState.county : null,
			useCountyState ? parsedCountyState.state : null,
		],
		queryFn: async () => {
			let requestParams: Pick<
				NearbyPropertiesRequest,
				"latitude" | "longitude" | "county" | "state"
			>;

			if (useAddress && address) {
				requestParams = {
					latitude: address.latitude!,
					longitude: address.longitude!,
				};
			} else if (
				useCountyState &&
				parsedCountyState.county &&
				parsedCountyState.state
			) {
				requestParams = {
					county: parsedCountyState.county,
					state: parsedCountyState.state,
				};
			} else {
				throw new Error("No valid search criteria provided");
			}

			if (canUseAuthenticatedApi) {
				return await fetchNearbyProperties({
					...requestParams,
					page: 1,
					pageSize: MAX_PLAYGROUND_RECORDS,
				});
			}
			return await fetchNearbyPropertiesUnauthenticated(requestParams);
		},
		enabled: useAddress || useCountyState,
	});

	// Update loading state
	useMemo(() => {
		onLoadingChange?.(isLoading);
	}, [isLoading, onLoadingChange]);

	// Normalize properties
	const properties = useMemo<NormalizedPropertyRecord[]>(() => {
		if (!data) return [];
		return data.data
			.slice(0, MAX_PLAYGROUND_RECORDS)
			.map(normalizePropertyRecord);
	}, [data]);

	const handleViewProperty = useCallback(
		(property: NormalizedPropertyRecord) => {
			const { state, county, propertyId } = property;
			if (state && county && propertyId) {
				const url = `/${state.toLowerCase()}/${county.toLowerCase().replace(/\s+/g, "-")}/property/${propertyId}`;
				window.open(url, "_blank");
			}
		},
		[]
	);

	const handleViewParcelData = useCallback(
		(property: NormalizedPropertyRecord) => {
			if (!property.parcelData) {
				return;
			}
			setSelectedParcel(property.parcelData as ParcelData);
			setSelectedProperty(property);
			setIsParcelViewerOpen(true);
		},
		[]
	);

	// Blur restricted content only when signed out or unsubscribed
	const shouldBlurRestrictedContent = !isSignedIn || !isSubscribed;

	// Dynamically generate columns from _displayNames (same as PropertiesTable)
	const columns = useMemo<MRT_ColumnDef<NormalizedPropertyRecord>[]>(() => {
		if (properties.length === 0) return [];

		// Get display names from the first record
		const displayNames = properties[0]._displayNames;
		const columns: MRT_ColumnDef<NormalizedPropertyRecord>[] = [];

		// Track which columns have shown a badge already (shared across all columns)
		const badgeShownForColumn = new Map<string, boolean>();

		// Helper for locked cells with badge (first occurrence) - only when blur applies
		const renderLockedCellWithBadge = () => (
			<div className="flex items-center gap-2">
				<span className="blur-sm select-none text-gray-400">
					Hidden Content
				</span>
				<Badge className="bg-[#A7FF5E] text-[#16251c] hover:bg-[#A7FF5E] border-none px-1.5 py-0.5 h-5 text-[10px] font-bold tracking-wider flex-shrink-0">
					PRO
				</Badge>
			</div>
		);

		// Helper for locked cells without badge (subsequent occurrences)
		const renderLockedCellBlurred = () => (
			<span className="blur-sm select-none text-gray-400">
				Hidden Content
			</span>
		);

		// Iterate through displayNames to create columns
		Object.entries(displayNames).forEach(([key, displayName]) => {
			// Skip _displayNames itself or if displayName is undefined
			if (key === "_displayNames" || !displayName) return;

			// Special handling for parcelData - show button
			if (key === "parcelData") {
				columns.push({
					id: "parcelData",
					accessorKey: "parcelData",
					header: displayName,
					Header: () => (
						<span
							title={displayName}
							data-testid="table-column-header"
							style={{
								overflow: "hidden",
								textOverflow: "ellipsis",
								whiteSpace: "nowrap",
								maxWidth: "100%",
							}}
						>
							{displayName}
						</span>
					),
					size: 190,
					Cell: ({ row }) => {
						const hasParcelData = Boolean(row.original.parcelData);

						return (
							<div className={styles.parcelActions}>
								<button
									type="button"
									data-testid="parcel-button"
									className={styles.parcelButton}
									onClick={(e) => {
										e.stopPropagation();
										if (row.original.parcelData) {
											handleViewParcelData(row.original);
										}
									}}
									disabled={!hasParcelData}
								>
									View Parcel
								</button>
								<PropertyPhotosButton
									hasPhotos={
										Array.isArray(row.original?.photos) &&
										row.original?.photos?.length > 0
									}
									onClick={() =>
										handlePhotoClick(row.original)
									}
									isLoading={
										checkingPhotoRequestId ===
										(row.original.propertyId ??
											row.original.id)
									}
								/>
							</div>
						);
					},
				});
				return;
			}

			// Special handling for address - wrap text
			if (key === "address") {
				const headerName = displayName || "Address";
				columns.push({
					accessorKey: key,
					header: headerName,
					Header: () => (
						<span
							title={headerName}
							data-testid="table-column-header"
							style={{
								overflow: "hidden",
								textOverflow: "ellipsis",
								whiteSpace: "nowrap",
								maxWidth: "100%",
							}}
						>
							{headerName}
						</span>
					),
					size: 300,
					Cell: ({ cell }) => {
						const value = String(cell.getValue() ?? "");
						if (
							value.toLowerCase() === "member only" &&
							shouldBlurRestrictedContent
						) {
							const columnId = "address";
							const showBadge =
								!badgeShownForColumn.get(columnId);
							if (showBadge) {
								badgeShownForColumn.set(columnId, true);
								return renderLockedCellWithBadge();
							}
							return renderLockedCellBlurred();
						}
						return (
							<div
								className={styles.addressCell}
								data-testid="playground-address-cell"
							>
								<span className={styles.addressText}>
									{value}
								</span>
							</div>
						);
					},
				});
				return;
			}

			// Special handling for propertyId - add icon
			if (key === "propertyId") {
				const headerName = displayName || "Property ID";
				columns.push({
					accessorKey: key,
					header: headerName,
					Header: () => (
						<span
							title={headerName}
							data-testid="table-column-header"
							style={{
								overflow: "hidden",
								textOverflow: "ellipsis",
								whiteSpace: "nowrap",
								maxWidth: "100%",
							}}
						>
							{headerName}
						</span>
					),
					size: 300,
					Cell: ({ cell }) => {
						const value = String(cell.getValue() ?? "");
						if (
							value.toLowerCase() === "member only" &&
							shouldBlurRestrictedContent
						) {
							const columnId = "propertyId";
							const showBadge =
								!badgeShownForColumn.get(columnId);
							if (showBadge) {
								badgeShownForColumn.set(columnId, true);
								return renderLockedCellWithBadge();
							}
							return renderLockedCellBlurred();
						}
						return (
							<div className={styles.propertyIdCell}>
								<Building2 className={styles.propertyIdIcon} />
								<span className={styles.propertyIdText}>
									{value}
								</span>
							</div>
						);
					},
				});
				return;
			}

			// Special handling for links - make them clickable
			if (key === "propertyLink" || key === "civilViewLink") {
				const headerName =
					displayName ||
					(key === "propertyLink"
						? "Property Link"
						: "Civil View Link");
				columns.push({
					accessorKey: key,
					header: headerName,
					Header: () => (
						<span
							title={headerName}
							style={{
								overflow: "hidden",
								textOverflow: "ellipsis",
								whiteSpace: "nowrap",
								maxWidth: "100%",
							}}
						>
							{headerName}
						</span>
					),
					size: 200,
					Cell: ({ cell }) => {
						const value = cell.getValue<
							string | null | undefined
						>();
						if (!value) return null;
						if (
							value.toLowerCase() === "member only" &&
							shouldBlurRestrictedContent
						) {
							const columnId = key;
							const showBadge =
								!badgeShownForColumn.get(columnId);
							if (showBadge) {
								badgeShownForColumn.set(columnId, true);
								return renderLockedCellWithBadge();
							}
							return renderLockedCellBlurred();
						}
						return (
							<a
								href={value}
								target="_blank"
								rel="noopener noreferrer"
								className={styles.linkCell}
								onClick={(e) => e.stopPropagation()}
							>
								<span className={styles.linkCellText}>
									{value}
								</span>
								<ExternalLink
									className={styles.externalLinkIcon}
									size={14}
								/>
							</a>
						);
					},
				});
				return;
			}

			// Default column for other fields
			const headerName = displayName || key;
			columns.push({
				accessorKey: key,
				header: headerName,
				Header: () => (
					<span
						title={headerName}
						data-testid="table-column-header"
						style={{
							overflow: "hidden",
							textOverflow: "ellipsis",
							whiteSpace: "nowrap",
							maxWidth: "100%",
						}}
					>
						{headerName}
					</span>
				),
				size: 150,
				Cell: ({ cell }) => {
					const value = cell.getValue();
					if (value === null || value === undefined) return null;
					const stringValue = String(value);
					if (
						stringValue.toLowerCase() === "member only" &&
						shouldBlurRestrictedContent
					) {
						const columnId = key;
						const showBadge = !badgeShownForColumn.get(columnId);
						if (showBadge) {
							badgeShownForColumn.set(columnId, true);
							return renderLockedCellWithBadge();
						}
						return renderLockedCellBlurred();
					}
					return (
						<span className={styles.cellText}>{stringValue}</span>
					);
				},
			});
		});

		return columns;
	}, [
		properties,
		handleViewParcelData,
		shouldBlurRestrictedContent,
		handlePhotoClick,
		checkingPhotoRequestId,
	]);

	const table = useMaterialReactTable({
		columns,
		data: properties,
		enablePagination: false, // No pagination for playground
		enableRowSelection: false,
		enableDensityToggle: true,
		enableFullScreenToggle: false,
		enableGlobalFilter: true,
		enableColumnFilters: false,

		initialState: {
			density: "compact",
			columnVisibility: {
				// Hide Tax Lien Vest internal IDs by default
				id: false,
				version: false,
				propertyId: false,
			},
		},
		muiTableBodyRowProps: ({ row }) => ({
			onClick: () => {
				handleViewProperty(row.original);
			},
			sx: {
				cursor: "pointer",
				"&:hover": {
					backgroundColor: "#f9fafb",
				},
			},
		}),
		muiTablePaperProps: {
			sx: {
				backgroundColor: "#ffffff",
				backgroundImage: "none",
				boxShadow: "none",
			},
		},
		muiTableContainerProps: {
			sx: {
				backgroundColor: "#ffffff",
				overflowX: "auto",
				overflowY: "visible",
			},
		},
		muiTableHeadCellProps: {
			sx: {
				backgroundColor: "#f9fafb",
				color: "#111827",
				borderBottom: "2px solid #e5e7eb",
				"& .Mui-TableHeadCell-Content": {
					color: "#111827",
				},
				"& .MuiSvgIcon-root": {
					color: "#6b7280",
				},
			},
		},
		muiTableBodyCellProps: {
			sx: {
				backgroundColor: "#ffffff",
				color: "#374151",
				borderBottom: "1px solid #f3f4f6",
			},
		},
		muiTopToolbarProps: {
			sx: {
				backgroundColor: "#ffffff",
				borderBottom: "1px solid #e5e7eb",
				overflow: "visible",
				"& .MuiIconButton-root": {
					color: "#6b7280",
					"&:hover": {
						backgroundColor: "#f9fafb",
						color: "#A7FF5E",
					},
				},
				"& .MuiInputBase-root": {
					color: "#111827",
					backgroundColor: "#ffffff",
					border: "1px solid #e5e7eb",
					borderRadius: "8px",
					"&:hover": {
						borderColor: "#A7FF5E",
					},
				},
				"& .MuiSvgIcon-root": {
					color: "#6b7280",
				},
			},
		},
		muiBottomToolbarProps: {
			sx: {
				display: "none", // Hide pagination toolbar
			},
		},
		renderTopToolbarCustomActions: () => {
			const canExport = isSignedIn && isSubscribed;
			return (
				<div
					className="flex gap-2 p-2"
					data-testid="playground-toolbar"
				>
					<button
						type="button"
						disabled={!canExport || exportingType !== null}
						onClick={async () => {
							if (!canExport || properties.length === 0) return;
							setExportingType("excel");
							try {
								await exportPropertiesToExcel(properties);
							} finally {
								setExportingType(null);
							}
						}}
						className={
							canExport
								? "bg-gray-200 text-gray-800 hover:bg-gray-300 px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-colors cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
								: "bg-gray-200 text-gray-500 cursor-not-allowed relative group px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2"
						}
						data-testid="export-csv-button"
					>
						{exportingType === "excel" ? (
							<Loader2 className="w-4 h-4 animate-spin" />
						) : canExport ? (
							<FileText className="w-4 h-4" />
						) : (
							<Lock className="w-4 h-4" />
						)}
						Export CSV
						{!canExport && (
							<div
								className="absolute top-full left-1/2 -translate-x-1/2 mt-2 px-3 py-1.5 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-50"
								data-testid="export-csv-tooltip"
							>
								{!isSignedIn
									? "Sign up to export"
									: "Subscribe to export"}
							</div>
						)}
					</button>
					<button
						type="button"
						disabled={!canExport || exportingType !== null}
						onClick={async () => {
							if (!canExport || properties.length === 0) return;
							setExportingType("propstream");
							try {
								await exportPropertiesToPropstream(properties);
							} finally {
								setExportingType(null);
							}
						}}
						className={
							canExport
								? "border border-gray-300 text-gray-800 hover:bg-gray-100 px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-colors cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
								: "border border-gray-300 text-gray-500 cursor-not-allowed relative group px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2"
						}
						data-testid="export-propstream-button"
					>
						{exportingType === "propstream" ? (
							<Loader2 className="w-4 h-4 animate-spin" />
						) : canExport ? (
							<ExternalLink className="w-4 h-4" />
						) : (
							<Lock className="w-4 h-4" />
						)}
						PropStream
						{!canExport && (
							<div
								className="absolute top-full left-1/2 -translate-x-1/2 mt-2 px-3 py-1.5 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-[100]"
								data-testid="export-propstream-tooltip"
							>
								{!isSignedIn
									? "Sign up to access"
									: "Subscribe to access"}
							</div>
						)}
					</button>
				</div>
			);
		},
	});

	if (isLoading) {
		return (
			<div className="flex items-center justify-center min-h-[60vh]">
				<div className="text-center">
					<Loader2 className="w-8 h-8 animate-spin text-[#A7FF5E] mx-auto mb-4" />
					<p className="text-gray-600">Loading properties...</p>
				</div>
			</div>
		);
	}

	if (isError) {
		return (
			<div className="flex items-center justify-center min-h-[60vh]">
				<div className="text-center">
					<AlertCircle className="w-8 h-8 text-red-500 mx-auto mb-4" />
					<p className="text-gray-900 font-semibold mb-2">
						Error loading properties
					</p>
					<p className="text-gray-600 text-sm">
						{error instanceof Error
							? error.message
							: "An error occurred"}
					</p>
				</div>
			</div>
		);
	}

	return (
		<div className="min-h-screen bg-white p-8 pb-24">
			<div className="max-w-[1800px] mx-auto mb-20">
				{/* Header */}
				<div className="mb-6 flex items-start justify-between">
					<div className="flex-1">
						<div className="flex items-center gap-4 mb-3">
							<h2 className="text-gray-900 text-4xl">
								Properties Database
							</h2>

							{/* Blinking Playground Indicator - only when signed out or unsubscribed */}
							{(!isSignedIn || !isSubscribed) && (
								<div className="flex items-center gap-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white px-4 py-2 rounded-full shadow-lg">
									<div className="relative w-2.5 h-2.5">
										<div className="absolute inset-0 bg-white rounded-full animate-ping opacity-75" />
										<div className="absolute inset-0 bg-white rounded-full" />
									</div>
									<span className="font-bold text-sm tracking-wide uppercase">
										You are in Playground
									</span>
								</div>
							)}
						</div>

						<p className="text-gray-600 text-lg mb-4">
							{useAddress
								? `Exploring properties near ${address?.formattedAddress || "selected address"}`
								: useCountyState
									? `Exploring properties in ${countyState}`
									: "Exploring sample properties"}
							. Click on any row or use the &quot;View
							Parcel&quot; button to view the property details
							page.
						</p>

						{/* Limitation Notice - only when signed out or unsubscribed */}
						{(!isSignedIn || !isSubscribed) && (
							<div className="bg-amber-50 border-l-4 border-amber-500 p-4 rounded-lg">
								<div className="flex items-start gap-3">
									<AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
									<div className="flex-1">
										<p
											className="text-amber-900 text-sm"
											data-testid="playground-mode-message"
										>
											<span className="font-semibold">
												Playground Mode:
											</span>{" "}
											You&apos;re viewing{" "}
											{MAX_PLAYGROUND_RECORDS} of 1000s+
											available properties.{" "}
											<button
												onClick={onSignUpClick}
												disabled={isUpgrading}
												data-testid="playground-signup-unlimited-button"
												className="ml-2 text-amber-700 underline hover:text-amber-900 font-semibold cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
											>
												Sign up for unlimited access
											</button>
										</p>
									</div>
								</div>
							</div>
						)}
					</div>
				</div>

				{/* Table */}
				<div className="bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-sm">
					<MaterialReactTable table={table} />
				</div>

				{/* Bottom Info Banner - only when signed out or unsubscribed */}
				{(!isSignedIn || !isSubscribed) && (
					<div className="mt-6 bg-gray-50 border border-gray-200 rounded-xl p-6">
						<div className="flex items-center justify-between">
							<div className="flex items-start gap-3">
								<Sparkles className="w-6 h-6 text-[#A7FF5E] flex-shrink-0 mt-1" />
								<div>
									<h3 className="text-gray-900 font-semibold mb-1">
										Ready to unlock the full database?
									</h3>
									<p className="text-gray-600 text-sm">
										Get access to 1000s+ properties, export
										tools, map view, auction calendar, and
										advanced filtering.
									</p>
								</div>
							</div>
							<button
								onClick={onSignUpClick}
								disabled={isUpgrading}
								className="bg-[#A7FF5E] hover:bg-[#cbf7a6] text-[#16251c] px-6 py-3 rounded-xl font-bold transition-all shadow-md hover:shadow-lg whitespace-nowrap border-2 border-[#16251c]/10 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-[#A7FF5E]"
							>
								{isUpgrading
									? "Processing..."
									: "Start Free Trial"}
							</button>
						</div>
					</div>
				)}
			</div>

			{/* Parcel Data Viewer Modal */}
			<ParcelDataViewer
				isOpen={isParcelViewerOpen}
				onClose={() => setIsParcelViewerOpen(false)}
				parcelData={selectedParcel}
				scrapedParcelData={
					(
						selectedProperty as {
							scrapedParcelData?: ScrapedParcelData;
						}
					)?.scrapedParcelData
				}
				propertyId={selectedProperty?.propertyId}
				realEstateId={selectedProperty?.id}
				state={selectedProperty?.state}
				county={selectedProperty?.county}
			/>

			{photoModalOpen && selectedProperty && (
				<>
					{Array.isArray(selectedProperty.medias) &&
					selectedProperty.medias.length > 0 ? (
						<ViewPhotosModal
							isOpen={photoModalOpen}
							onClose={() => {
								setPhotoModalOpen(false);
								setSelectedProperty(null);
							}}
							address={selectedProperty.address || ""}
							photos={selectedProperty.medias.map(
								(url) =>
									`${process.env.NEXT_PUBLIC_MEDIA_BASE_URL}${url}`
							)}
						/>
					) : (
						<RequestPhotosModal
							isOpen={photoModalOpen}
							onClose={() => {
								setPhotoModalOpen(false);
								setSelectedProperty(null);
							}}
							properties={[
								(() => {
									const parcelData =
										(selectedProperty.parcelData as Record<
											string,
											unknown
										> | null) ?? null;
									const city =
										(typeof parcelData?.scity ===
											"string" &&
											parcelData.scity) ||
										(typeof parcelData?.city === "string" &&
											parcelData.city) ||
										(selectedProperty.region ?? "") ||
										"";
									return {
										id: selectedProperty.id,
										address: selectedProperty.address || "",
										city,
										state: selectedProperty.state,
										county: selectedProperty.county,
										zip: selectedProperty.zip,
										latitude: selectedProperty.latitude,
										longitude: selectedProperty.longitude,
									};
								})(),
							]}
						/>
					)}
				</>
			)}

			{/* CSS for blinking animation */}
			<style>{`
        @keyframes ping {
          75%, 100% {
            transform: scale(2);
            opacity: 0;
          }
        }
        .animate-ping {
          animation: ping 1.5s cubic-bezier(0, 0, 0.2, 1) infinite;
        }
      `}</style>
		</div>
	);
}
